import {
  Component, OnInit, OnDestroy, ViewChild, EventEmitter,
  ElementRef, AfterViewInit, Input, Output, NgZone, HostListener
} from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { TranslateService } from 'ng2-translate';

import * as d3 from 'd3';
import * as hsv from 'd3-hsv';
import * as _ from 'lodash';

import { Size } from '../../../domain/models/size';
import { RouteDetailService } from '../../services/route-detail.service';
import { DateSettings } from '../../viewModels/date-settings-viewmodel';
import { SpectrogramData, SpectrogramValue, SpectrogramSectionValue } from '../../../domain/models/spectrogramData';
import { rgbToHex } from '../../../../utils/color-converter';

@Component({
  selector: 'app-heatmap',
  templateUrl: './heatmap.component.html',
  styleUrls: ['./heatmap.component.scss']
})
export class HeatmapComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() dateChangedObs: Observable<DateSettings>;
  @Input() downloadObs: Observable<any>;
  @Input() scaleChanged: Observable<number>;
  @Input() heatScale: number;

  @Input() compareObs: Observable<string[]>;
  @Output() onCanvasReady: EventEmitter<any> = new EventEmitter();
  @Output() onHeatmapStatusChanged: EventEmitter<boolean> = new EventEmitter();

  public jobId: string;
  public date: Date;
  private routeLength: number;

  public sliderValue: number;
  public maxIndexHeatmap: number;

  public data: SpectrogramData;
  public colors: any[];
  public maxDelay: number;
  public centerDelay: number;
  public streetviewUnavailable: boolean;
  public spectrogramType: string;
  public imageReady: boolean;
  private colorScale: any;
  private totalDistance: number;
  private subscriptions: Subscription[] = [];
  private noData: boolean;
  private customScaleSet = false;
  private maxPixels = 30000;
  private pixelScale = 1;

  private autoplayInterval: any;

  private comparedRoutes: SpectrogramData[];

  private spectrogramOnMapEnabled: boolean;

  private heatmapSelected: boolean;
  private heatmapSelectionX: number;
  private heatmapSelectionY: number;
  private heatmapSelectionWidth: number;
  private heatmapSelectionHeight: number;
  private defaultHeatScale: number;

  @ViewChild('heatmap') heatmap: ElementRef;
  @ViewChild('heatmapAxis') heatmapAxis: ElementRef;

  @ViewChild('container') container: ElementRef;

  constructor(private routeDetailService: RouteDetailService,
    private translate: TranslateService,
    private zone: NgZone) { }

  ngOnInit() {
    this.data = new SpectrogramData();
    this.colors = [];
    this.streetviewUnavailable = false;
    this.noData = false;
    this.spectrogramType = 'delay';
    this.imageReady = false;
    this.comparedRoutes = [];
    this.sliderValue = 0;
    this.spectrogramOnMapEnabled = false;

    this.heatmapSelected = false;
    this.heatmapSelectionX = 0;
    this.heatmapSelectionY = 0;
    this.heatmapSelectionWidth = 53;
    this.heatmapSelectionHeight = 60;

    if (this.heatScale) {
      this.maxDelay = this.heatScale;
      this.customScaleSet = true;
    }

    if (this.downloadObs) {
      this.subscriptions.push(this.downloadObs.subscribe(() => {
        this.onCanvasReady.emit(this.container);
      }));
    }
    this.subscribeScaleChanged();
  }

  subscribeScaleChanged(): void {
    this.subscriptions.push(this.scaleChanged.debounceTime(500).subscribe(() => {
      if (this.heatScale) {
        this.maxDelay = this.heatScale;
        this.customScaleSet = true;
      } else {
        this.maxDelay = this.defaultHeatScale;
        this.customScaleSet = false;
      }
      this.drawMap();
    }));
  }

  ngAfterViewInit(): void {
    this.subscribeDateChanged();
    this.subscribeCompare();
  }

  subscribeDateChanged(): void {
    this.subscriptions.push(this.dateChangedObs.subscribe((settings: DateSettings) => {
      this.clearMap();
      this.setHeatmapStatus(false);
      if (settings.routeLength !== undefined && (settings.routeLength !== this.routeLength || settings.routeId !== this.jobId || settings.date !== this.date)) {
        this.jobId = settings.routeId;
        this.date = settings.date;
        this.routeLength = settings.routeLength;
        this.getSpectogramData();
      }
    }));
  }

  subscribeCompare(): void {
    this.subscriptions.push(this.compareObs.subscribe((ids: string[]) => {
      if (ids) {
        _.each(ids, (id: string) => {
          this.routeDetailService.getSpectogramData(id, this.date)
            .subscribe((result: SpectrogramData) => {
              this.comparedRoutes.push(result);
              this.drawSpectrogramOnMap();
            });
        });
      } else {
        this.comparedRoutes = [];
        this.drawSpectrogramOnMap();
      }
    }));
  }

  sliderChanged(): void {
    this.drawSpectrogramOnMap();
  }

  startAutoPlay(sec: number): void {
    this.stopAutoPlay();
    this.autoplayInterval = setInterval(() => {
      this.sliderValue++;
      if (this.sliderValue > this.maxIndexHeatmap) {
        this.sliderValue = 0;
      }
      this.sliderChanged();
    }, sec);
  }

  stopAutoPlay(): void {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
    }
  }

  getLabelByIndex(): string {
    if (this.data && this.data.spectrogramValues && this.sliderValue !== undefined && this.data.spectrogramValues[this.sliderValue]) {
      return this.data.spectrogramValues[this.sliderValue].time;
    } else {
      return '';
    }
  }

  redrawSpectrogram(): void {
    this.maxDelay = this.findMaxDelay(this.data);
    this.centerDelay = Math.round(this.maxDelay * 50) / 100;
    this.drawMap();
  }

  getSpectogramData(): void {
    if (!this.jobId) {
      return;
    }

    this.routeDetailService.getSpectogramData(this.jobId, this.date)
      .subscribe((data: SpectrogramData) => {
        if (data) {
          this.noData = false;

          this.data = data;
          this.maxIndexHeatmap = this.data.spectrogramValues.length - 1;

          if (this.data.overviewPath !== undefined && this.data.overviewPath.length > 0) {
            this.spectrogramOnMapEnabled = true;
          }

          this.totalDistance = this.routeLength;

          this.defaultHeatScale = this.findMaxDelay(data);
          if (!this.customScaleSet) {
            this.maxDelay = this.defaultHeatScale;
          }
          this.centerDelay = Math.round(this.maxDelay * 50) / 100;

          setTimeout(() => {
            this.drawMap();
          }, 0);

        } else {
          this.noData = true;
        }
      },
        (error: any) => {
          this.noData = true;
        });
  }

  drawSpectrogramOnMap(): void {
    if (!this.spectrogramOnMapEnabled) {
      return;
    }
    if (!this.maxDelay || !this.colors || this.colors.length === 0) {
      setTimeout(() => { this.drawSpectrogramOnMap(); }, 500);
    } else if (this.data.spectrogramValues[this.sliderValue].values.length > 0) {
      this.routeDetailService.clearCurrentRouteFromMapDelayed();
      this.routeDetailService.drawMapSpectrogram(this.data.overviewPath,
        this.data.spectrogramValues[this.sliderValue], this.maxDelay, this.colors, this.spectrogramType);
      if (this.comparedRoutes.length > 0) {
        this.routeDetailService.clearOtherRoutesFromMap();
        _.each(this.comparedRoutes, (routeData: SpectrogramData) => {
          this.routeDetailService.drawMapSpectrogram(routeData.overviewPath, routeData.spectrogramValues[this.sliderValue], this.maxDelay, this.colors, this.spectrogramType);
        });
      }
    }
  }

  findMaxDelay(data: SpectrogramData): number {
    let delay = 1;
    _.each(data.spectrogramValues, (value: SpectrogramValue) => {
      _.each(value.values, (sectionValue: SpectrogramSectionValue) => {
        if (sectionValue[this.spectrogramType] > delay) {
          delay = Math.ceil(sectionValue[this.spectrogramType]);
        }
      });
    });
    return delay;
  }

  onClick(event: any): void {
    let ctx: CanvasRenderingContext2D = this.heatmap.nativeElement.getContext('2d');
    let canvas = ctx.canvas;
    let rect = canvas.getBoundingClientRect();
    let xClicked = event.clientX - rect.left;
    let yClicked = event.clientY - rect.top;
    let height = canvas.clientHeight;
    let scale = height / this.totalDistance;
    let yValue = Math.round(yClicked / scale);
    let length = this.totalDistance - yValue;

    let firstWithData = _.find(this.data.spectrogramValues, (value: SpectrogramValue) => {
      return value.values.length > 0;
    });

    let datapoint = _.find(firstWithData.values, (value: SpectrogramSectionValue) => {
      return value.distanceInRoute <= length && length <= (value.distanceInRoute + value.distance);
    });
    let startPoint = datapoint.startLocation.split(',');
    let location = { lat: parseFloat(startPoint[0]), lng: parseFloat(startPoint[1]) };
    this.routeDetailService.checkStreetViewAvailability(location)
      .subscribe((result: boolean) => {
        this.zone.run(() => {
          if (result) {
            this.routeDetailService.enableSplitView(location);
          } else {
            this.streetviewUnavailable = true;
            this.routeDetailService.drawMarker('../../../../assets/marker-map-small.png', new Size(40, 45), location);
            setTimeout(() => {
              this.streetviewUnavailable = false;
            }, 5000);
          }
        });
      });

    this.heatmapSelected = true;
    this.heatmapSelectionX = xClicked - (this.heatmapSelectionWidth / 2) + 70;
    this.heatmapSelectionY = yClicked - this.heatmapSelectionHeight + 20;
  }

  @HostListener('document:click', ['$event']) clickedOutside($event) {
    let target = $event.target;
    if (!_.isUndefined(target) && target.id === 'route-detail-outline' || target.localName === 'app-route-detail') {
      this.heatmapSelected = false;
      this.routeDetailService.closeSplitView();
      this.routeDetailService.clearMarkers();
    }
  }

  getHeatmapSelectionX(): number {
    return this.heatmapSelectionX;
  }

  getHeatmapSelectionY(): number {
    return this.heatmapSelectionY;
  }

  getHeatmapSelectionWidth(): number {
    return this.heatmapSelectionWidth;
  }

  getHeatmapSelectionHeight(): number {
    return this.heatmapSelectionHeight;
  }

  heatmapIsSelected(): boolean {
    return this.heatmapSelected;
  }

  onResize(): void {
    this.heatmapSelected = false;
    this.drawMap();
  }

  noDataAvailable(): boolean {
    return this.noData;
  }

  private clearMap() {
    if (this.noData) { return; }
    let context = this.heatmap.nativeElement.getContext('2d');
    context.clearRect(0, 0, this.heatmap.nativeElement.width, this.heatmap.nativeElement.height);
    d3.selectAll('svg > *').remove();
  }

  private setHeatmapStatus(imageReady: boolean) {
    this.imageReady = imageReady;
    this.onHeatmapStatusChanged.emit(this.imageReady);
  }

  private drawMap() {
    let context = this.heatmap.nativeElement.getContext('2d');

    setTimeout(() => {
      let color1 = hsv.hsv(209, 0.88, 0.78);
      let color2 = hsv.hsv(58, 0.97, 0.96);
      let color3 = hsv.hsv(0, 1, 0.95);

      let i0 = hsv.interpolateHsvLong(color1, color2);
      let i1 = hsv.interpolateHsvLong(color2, color3);

      if (this.spectrogramType === 'realTravelSpeed') {
        i0 = hsv.interpolateHsvLong(color3, color2);
        i1 = hsv.interpolateHsvLong(color2, color1);
      }

      let interpolateTerrain = function (t) { return t < 0.5 ? i0(t * 2) : i1((t - 0.5) * 2); };
      this.colorScale = d3.scaleSequential(interpolateTerrain).domain([0, this.maxDelay]);

      if (this.totalDistance > this.maxPixels) {
        this.pixelScale = Math.ceil(this.totalDistance / this.maxPixels);
      }

      let numberofPixels = Math.round(this.totalDistance / this.pixelScale);

      let m = this.data.spectrogramValues.length;
      let n = numberofPixels;
      let data = [];
      let startTimeString = this.data.spectrogramValues[0].time;
      let endTimeString = this.data.spectrogramValues[this.data.spectrogramValues.length - 1].time;

      let startTime = this.parseTimeString(startTimeString);
      let endTime = this.parseTimeString(endTimeString);

      let canvas = context.canvas;
      canvas.style.marginTop = '20px';

      let clientHeight = canvas.clientHeight;
      let clientWidth = canvas.clientWidth;
      let margin = { top: 20, right: 70, bottom: 125, left: 20 };

      d3.select(this.heatmap.nativeElement)
        .attr('width', m)
        .attr('height', n)
        .append('g')
        .style('font-size', '12px')
        .attr('transform', 'translate(0,' + n + ')');

      d3.select(this.heatmapAxis.nativeElement)
        .selectAll('*')
        .remove();

      let svg = d3.select(this.heatmapAxis.nativeElement)
        .append('svg')
        .attr('width', clientWidth + margin.left + margin.right)
        .attr('height', clientHeight + margin.top + margin.bottom)
        .attr('xmlns', 'http://www.w3.org/2000/svg')
        .append('g')
        .attr('transform', 'translate(20,20)');

      this.normalizeData(data, n, m);
      this.drawAxis(svg, startTime, endTime, clientHeight, clientWidth, m, n);

      let image = context.createImageData(m, n);
      this.setImageData(image, data, m, n);
      context.putImageData(image, 0, 0);

      this.drawColorScale(svg, clientHeight, clientWidth);

      this.drawSpectrogramOnMap();
      this.setHeatmapStatus(true);
    }, 250);
  }

  private normalizeData(data: any, n: any, m: any): void {
    let normalizedData = [];

    _.each(this.data.spectrogramValues, (set: SpectrogramValue) => {
      let subSetData = [];
      _.each(set.values, (subSet: SpectrogramSectionValue) => {
        let heightInPixels = Math.round(subSet.distance / this.pixelScale);
        for (let i = 0; i <= heightInPixels; i++) {
          subSetData.push(subSet[this.spectrogramType]);
        }
      });
      normalizedData.push(subSetData);
    });
    for (let i = n - 1; i >= 0; i--) {
      for (let j = 0; j < normalizedData.length; j++) {
        if (normalizedData[j] === undefined || normalizedData[j][i] === undefined) {
          data.push(null);
        } else {
          data.push(normalizedData[j][i]);
        }
      }
    }
  }

  private drawAxis(svg: any, startTime: any, endTime: any, clientHeight: any, clientWidth: any, m: any, n: any): void {
    let formatTime = d3.timeFormat('%H:%M');
    let x = d3.scaleLinear()
      .domain([startTime, endTime])
      .range([0, clientWidth]);

    let ticks = this.calculateTicks(startTime, endTime);
    let xAxis = d3.axisBottom(x)
      .scale(x)
      .tickFormat(formatTime)
      .tickValues(ticks);

    let y = d3.scaleLinear()
      .range([clientHeight, 0])
      .domain([0, this.totalDistance]);

    let yAxis = d3.axisLeft(y)
      .tickValues(y.ticks(10).concat(y.domain()));

    let xLabel = this.translate.instant('TIME_LABEL');
    let yLabel = this.translate.instant('DISTANCE_LEGEND');

    svg.append('g')
      .style('font-size', '13px')
      .style('font-family', 'Roboto Condensed, sans-serif')
      .attr('transform', 'translate(' + 50 + ',' + clientHeight + ')')
      .call(xAxis);

    svg.append('g')
      .style('font-size', '13px')
      .style('font-family', 'Roboto Condensed, sans-serif')
      .attr('transform', 'translate(50,0)')
      .call(yAxis);

    svg.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', 0 - 10)
      .attr('x', 0 - (clientHeight / 2))
      .attr('dy', '1em')
      .style('text-anchor', 'middle')
      .style('font-family', 'Roboto Condensed, sans-serif')
      .style('font-size', '13px')
      .text(yLabel);

    svg.append('text')
      .attr('transform', 'translate(' + ((clientWidth / 2) + 40) + ' ,' + (clientHeight + 38) + ')')
      .style('text-anchor', 'middle')
      .style('font-family', 'Roboto Condensed, sans-serif')
      .style('font-size', '13px')
      .text(xLabel);
  }

  private drawColorScale(svg: any, clientHeight: any, clientWidth: any): void {
    this.initColorScale(this.maxDelay);

    let gradientBarX = 50;
    let gradientBarY = clientHeight + 65;
    let gradientBarHeight = 20;

    let gradientTitle = '';
    if (this.spectrogramType === 'delay') {
      gradientTitle = this.translate.instant('DELAY_SEC_KM');
    } else {
      gradientTitle = this.translate.instant('SPEED_KM_HOUR');
    }

    svg.append('text')
      .attr('transform', 'translate(' + gradientBarX + ' ,' + (gradientBarY - 5) + ')')
      .style('font-family', 'Roboto Condensed, sans-serif')
      .style('font-size', '13px')
      .text(gradientTitle);

    svg.append('defs')
      .append('linearGradient')
      .attr('id', 'heatmapGradient')
      .attr('x1', '0%').attr('y1', '0%')
      .attr('x2', '100%').attr('y2', '0%')
      .selectAll('stop')
      .data(this.colors)
      .enter().append('stop')
      .attr('offset', function (d) { return d.offset; })
      .attr('stop-color', function (d) { return d.color; });

    svg.append('rect')
      .attr('x', gradientBarX).attr('y', gradientBarY)
      .attr('width', clientWidth).attr('height', gradientBarHeight)
      .style('fill', 'url(#heatmapGradient)');

    let delayX = d3.scaleLinear()
      .domain([0, this.maxDelay])
      .range([0, clientWidth]);

    let delayValues = [0, this.centerDelay / 2, this.centerDelay, this.maxDelay * 3 / 4, this.maxDelay];
    let delayXAxis = d3.axisBottom(delayX)
      .scale(delayX)
      .tickValues(delayValues);

    svg.append('g')
      .attr('class', 'delay-axis')
      .attr('transform', 'translate(' + gradientBarX + ' ,' + (gradientBarY + 20) + ')')
      .style('font-family', 'Roboto Condensed, sans-serif')
      .style('font-size', '13px')
      .call(delayXAxis);
  }

  private setImageData(image: any, data: any, m: any, n: any): any {
    for (let j = 0, k = 0, l = 0; j < m; ++j) {
      for (let i = 0; i < n; ++i, ++k, l += 4) {
        let c = d3.rgb(this.colorScale(data[k]));
        if (data[k] >= this.maxDelay) {
          c = d3.rgb(this.colorScale(this.maxDelay));
        }
        if (data[k] == null) {
          image.data[l + 0] = 255;
          image.data[l + 1] = 255;
          image.data[l + 2] = 255;
          image.data[l + 3] = 255;
        } else {
          image.data[l + 0] = c.r;
          image.data[l + 1] = c.g;
          image.data[l + 2] = c.b;
          image.data[l + 3] = 255;
        }
      }
    }
  }

  private initColorScale(maxValue: number): void {
    this.colors = [];
    for (let i = 1; i <= 990; i++) {
      let offset = i / 990;
      let value = this.colorScale(offset * maxValue);
      let rgbColor = d3.rgb(value);
      let hexColor = { offset: offset, color: rgbToHex(rgbColor) };
      this.colors.push(hexColor);
    }
  }

  private calculateTicks(startTime: Date, endTime: Date): Date[] {
    let ticks = [];
    let interval = endTime.getTime() - startTime.getTime();
    let tickInterval = Math.round(interval / 6);

    ticks.push(startTime);
    for (let i = 1; i < 6; i++) {
      let date = new Date();
      date.setTime(startTime.getTime() + tickInterval * i);
      ticks.push(date);
    }
    ticks.push(endTime);

    return ticks;
  }

  private parseTimeString(timeString: string): Date {
    let timeArray = timeString.split(':');
    let date = new Date();
    date.setHours(parseInt(timeArray[0], 10));
    date.setMinutes(parseInt(timeArray[1], 10));

    return date;
  }
  showLoadingSpinner(): boolean {
    return !this.imageReady;
  }

  showSpectrogramOnMap(): boolean {
    return this.spectrogramOnMapEnabled;
  }

  ngOnDestroy() {
    this.routeDetailService.closeSplitView();
    this.routeDetailService.clearMarkers();
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
    }
    _.each(this.subscriptions, (sub: Subscription) => {
      sub.unsubscribe();
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/route-detail/components/heatmap/heatmap.component.ts